import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  formatRelative,
  useYoutubeAudit,
  useYoutubeOauthDisconnect,
  useYoutubeOauthStart,
  useYoutubeOauthStatus,
  useYoutubeQuota,
  useYoutubeRunSync,
  useYoutubeSyncStatus,
} from "../../../lib/youtube";

/**
 * Advanced page — the operator surface. Connection · Sync · Daily quota ·
 * Audit log, all on one scroll-down. Editors don't see this tab; only
 * roles with `youtube.connect` (admin / developer) reach it.
 */

const OAUTH_BANNER_MESSAGES: Record<
  string,
  { kind: "ok" | "warn" | "err"; text: string }
> = {
  connected: {
    kind: "ok",
    text: "Channel connected. You're now authorized to manage uploads, metadata, and playlists from here.",
  },
  denied: {
    kind: "warn",
    text: "Authorization cancelled — you (or another step) declined Google's consent screen.",
  },
  invalid: {
    kind: "err",
    text: "Authorization request was missing required parameters. Please try connecting again.",
  },
  "state-mismatch": {
    kind: "err",
    text: "Authorization session expired or didn't match. Start over and complete the consent within 10 minutes.",
  },
  "no-refresh": {
    kind: "err",
    text: "Google didn't return a refresh token. Disconnect the app at https://myaccount.google.com/permissions and try again.",
  },
  "no-channel": {
    kind: "err",
    text: "Couldn't read the connected Google account's YouTube channel. Make sure you authorized as the channel owner.",
  },
  failed: {
    kind: "err",
    text: "OAuth failed. See the audit log for details.",
  },
};

export default function YouTubeAdvanced() {
  const [params, setParams] = useSearchParams();
  const oauthFlag = params.get("oauth");
  const oauthReason = params.get("reason");

  useEffect(() => {
    if (oauthFlag) {
      const next = new URLSearchParams(params);
      next.delete("oauth");
      next.delete("reason");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthFlag]);

  const banner = oauthFlag ? OAUTH_BANNER_MESSAGES[oauthFlag] : null;

  return (
    <div className="yt-advanced">
      <header className="mb-4">
        <p className="page-eyebrow mb-1">Advanced</p>
        <h2 className="font-display-italic text-[1.5rem] leading-[1.2] text-[var(--color-ink)]">
          The plumbing.
        </h2>
        <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-1 max-w-[60ch]">
          Connection state, sync schedule, daily YouTube API quota, and the
          full ops audit log. None of this surfaces to editors.
        </p>
      </header>

      {banner && (
        <div className={`yt-banner is-${banner.kind} mb-4`}>
          <p>{banner.text}</p>
          {oauthReason && (
            <p className="yt-banner-reason font-grotesk">{oauthReason}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConnectionCard />
        <SyncCard />
        <QuotaCard />
      </div>

      <AuditSection />
    </div>
  );
}

// ---------------------------------------------------------------------------

function ConnectionCard() {
  const q = useYoutubeOauthStatus();
  const start = useYoutubeOauthStart();
  const disconnect = useYoutubeOauthDisconnect();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const status = q.data;
  const configured = !!status?.configured;
  const connected = !!status?.connected;

  return (
    <article className="yt-admin-card">
      <header className="yt-admin-card-head">
        <p className="page-eyebrow">Connection</p>
        <span
          className={`yt-pill ${connected ? "is-on" : configured ? "is-warn" : "is-off"}`}
        >
          {connected ? "Connected" : configured ? "Ready" : "Not yet"}
        </span>
      </header>

      {q.isLoading ? (
        <p className="yt-admin-empty">Checking…</p>
      ) : connected ? (
        <>
          <p className="yt-admin-headline">
            Authorized as{" "}
            <span className="font-display-italic">
              {status?.channelTitle ?? "the channel"}
            </span>
            .
          </p>
          <dl className="yt-admin-dl">
            <div>
              <dt>Connected</dt>
              <dd>{formatRelative(status?.connectedAt)}</dd>
            </div>
            {status?.refreshExpiresAt && (
              <div>
                <dt>Refresh expires</dt>
                <dd>{formatRelative(status.refreshExpiresAt)}</dd>
              </div>
            )}
          </dl>
          <div className="yt-admin-actions mt-2">
            {!confirmDisconnect ? (
              <button
                type="button"
                className="btn-app-ghost is-danger"
                onClick={() => setConfirmDisconnect(true)}
              >
                Disconnect
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-app-ghost"
                  onClick={() => setConfirmDisconnect(false)}
                  disabled={disconnect.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-app is-danger"
                  onClick={() => {
                    disconnect.mutate(undefined, {
                      onSuccess: () => setConfirmDisconnect(false),
                    });
                  }}
                  disabled={disconnect.isPending}
                >
                  {disconnect.isPending ? "Revoking…" : "Confirm disconnect"}
                </button>
              </>
            )}
          </div>
        </>
      ) : configured ? (
        <>
          <p className="yt-admin-headline">
            Ready to authorize.{" "}
            <span className="font-display-italic">One click from here.</span>
          </p>
          <p className="yt-admin-note">
            The channel owner connects once. After that, anyone with the
            permission can manage the channel from this dashboard — no Google
            account required.
          </p>
          <div className="yt-admin-actions">
            <button
              type="button"
              className="btn-app"
              onClick={() => start.mutate()}
              disabled={start.isPending}
            >
              {start.isPending ? "Starting…" : "Connect channel"}
            </button>
          </div>
          {start.error && (
            <p className="yt-admin-error mt-2">{start.error.message}</p>
          )}
        </>
      ) : (
        <>
          <p className="yt-admin-headline">
            Read-only mode.{" "}
            <span className="font-display-italic">No credentials yet.</span>
          </p>
          <p className="yt-admin-note">
            We're mirroring the public side of the channel via the API key.
            Adding OAuth credentials will turn on uploads, edits, and series
            management.
          </p>
        </>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------

function SyncCard() {
  const q = useYoutubeSyncStatus();
  const sync = useYoutubeRunSync();
  const [forceNext, setForceNext] = useState(false);

  const status = q.data;
  const ok = status?.lastFullSyncOk;
  const running = !!status?.currentlySyncing;
  const exhausted = !!status?.quotaExhausted;
  const onClick = () => {
    sync.mutate(forceNext ? { force: true } : undefined);
    setForceNext(false);
  };

  return (
    <article className="yt-admin-card">
      <header className="yt-admin-card-head">
        <p className="page-eyebrow">Sync</p>
        <span
          className={`yt-pill ${running ? "is-warn" : exhausted ? "is-off" : ok ? "is-on" : ok === false ? "is-off" : ""}`}
        >
          {running
            ? "Syncing…"
            : exhausted
              ? "Quota paused"
              : ok === true
                ? "Healthy"
                : ok === false
                  ? "Errored"
                  : "Idle"}
        </span>
      </header>

      {q.isLoading ? (
        <p className="yt-admin-empty">Checking sync state…</p>
      ) : !status ? (
        <p className="yt-admin-empty">No sync has run yet.</p>
      ) : (
        <>
          <p className="yt-admin-headline">
            <span className="tabular">{status.videosCount.toLocaleString()}</span>{" "}
            videos · <span className="tabular">{status.playlistsCount.toLocaleString()}</span>{" "}
            series.
          </p>
          <dl className="yt-admin-dl">
            <div>
              <dt>Last sync</dt>
              <dd>{formatRelative(status.lastFullSyncFinishedAt)}</dd>
            </div>
            <div>
              <dt>Channel</dt>
              <dd className="font-grotesk text-[0.7rem]">
                {status.channelId ?? "unresolved"}
              </dd>
            </div>
            {status.nextRetryAt && (
              <div>
                <dt>Next auto-retry</dt>
                <dd>{formatRelative(status.nextRetryAt)}</dd>
              </div>
            )}
          </dl>

          {status.lastError && (
            <div className="yt-admin-error">
              <p className="yt-admin-error-eyebrow">Last error</p>
              <p>{status.lastError}</p>
            </div>
          )}

          <div className="yt-admin-actions">
            <button
              type="button"
              className="btn-app"
              onClick={onClick}
              disabled={sync.isPending || running}
            >
              {sync.isPending ? "Refreshing…" : running ? "Already running" : "Refresh now"}
            </button>
            {!running && (
              <label className="yt-admin-checkbox">
                <input
                  type="checkbox"
                  checked={forceNext}
                  onChange={(e) => setForceNext(e.target.checked)}
                />
                <span>Force (clear stuck lock)</span>
              </label>
            )}
          </div>

          {sync.error && (
            <p className="yt-admin-error mt-3">{sync.error.message}</p>
          )}
          {sync.data && (
            <p className="yt-admin-note mt-3">
              {sync.data.ok
                ? `Refreshed in ${Math.round((sync.data.durationMs ?? 0) / 100) / 10}s — ${sync.data.videosCount} videos · ${sync.data.playlistsCount} series.`
                : sync.data.skipped
                  ? `Skipped (${sync.data.skipped}).`
                  : `Failed: ${sync.data.error ?? "unknown error"}.`}
            </p>
          )}
        </>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------

function QuotaCard() {
  const q = useYoutubeQuota();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const status = q.data;

  if (q.isLoading) {
    return (
      <article className="yt-admin-card">
        <header className="yt-admin-card-head">
          <p className="page-eyebrow">Daily quota</p>
        </header>
        <p className="yt-admin-empty">Checking…</p>
      </article>
    );
  }
  if (q.error || !status) {
    return (
      <article className="yt-admin-card">
        <header className="yt-admin-card-head">
          <p className="page-eyebrow">Daily quota</p>
          <span className="yt-pill is-off">Unavailable</span>
        </header>
        <p className="yt-admin-empty">
          Couldn't read the quota state.{" "}
          {q.error?.message ? (
            <span className="font-display-italic">{q.error.message}</span>
          ) : (
            "If you just upgraded the server, run db:migrate to add the youtube_quota_state table."
          )}
        </p>
      </article>
    );
  }

  // Defensive: validate the byAction shape before iterating. The server
  // returns a plain object with up to ~30 keys, but if anything upstream
  // (Drizzle JSON parsing, mid-flight upgrade) returns an unusual shape
  // we don't want to crash the page with a RangeError on Object.entries.
  const safeByAction =
    status.byAction && typeof status.byAction === "object" && !Array.isArray(status.byAction)
      ? status.byAction
      : {};
  const limitClamped = Math.max(1, status.unitsLimit || 10000);
  const usedClamped = Math.max(0, status.unitsUsed || 0);
  const ratio = usedClamped / limitClamped;
  const tone = ratio > 0.95 ? "danger" : ratio > 0.8 ? "warn" : "ok";

  // Cap to 100 entries defensively + filter out non-numeric values.
  const sortedActions = Object.entries(safeByAction)
    .filter(([, v]) => typeof v === "number" && Number.isFinite(v))
    .slice(0, 100)
    .sort((a, b) => Number(b[1]) - Number(a[1]));

  return (
    <article className="yt-admin-card">
      <header className="yt-admin-card-head">
        <p className="page-eyebrow">Daily quota</p>
        <span
          className={`yt-pill ${tone === "ok" ? "is-on" : tone === "warn" ? "is-warn" : "is-off"}`}
        >
          {tone === "ok"
            ? "Healthy"
            : tone === "warn"
              ? "Approaching limit"
              : "Near limit"}
        </span>
      </header>
      <p className="yt-admin-headline">
        <span className="tabular">{status.unitsUsed.toLocaleString()}</span>
        {" / "}
        <span className="tabular">{status.unitsLimit.toLocaleString()}</span>{" "}
        <span className="font-display-italic text-[var(--color-ink-mute)]">
          units today
        </span>
      </p>
      <div className={`yt-quota-bar yt-quota-${tone}`}>
        <div
          className="yt-quota-fill"
          style={{ width: `${Math.min(100, ratio * 100)}%` }}
        />
      </div>
      <p className="yt-admin-note font-display-italic">
        Resets {formatRelative(status.resetsAt)}.
      </p>
      <button
        type="button"
        className="row-action mt-1"
        onClick={() => setShowBreakdown((b) => !b)}
        aria-expanded={showBreakdown}
      >
        {showBreakdown ? "Hide breakdown" : "Show breakdown by action"}
      </button>
      {showBreakdown && (
        <ul className="yt-quota-breakdown mt-2">
          {sortedActions.length === 0 ? (
            <li className="yt-admin-empty">No usage today yet.</li>
          ) : (
            sortedActions.map(([action, units]) => (
              <li key={action}>
                <span className="font-grotesk">{action}</span>
                <span className="tabular">{units.toLocaleString()}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------

const ACTION_FILTERS = [
  { value: "", label: "All actions" },
  { value: "sync.run", label: "sync.run" },
  { value: "video.upload", label: "video.upload" },
  { value: "video.update", label: "video.update" },
  { value: "video.delete", label: "video.delete" },
  { value: "video.thumbnail", label: "video.thumbnail" },
  { value: "playlist.create", label: "playlist.create" },
  { value: "playlist.update", label: "playlist.update" },
  { value: "playlist.delete", label: "playlist.delete" },
  { value: "playlist_item.add", label: "playlist_item.add" },
  { value: "playlist_item.remove", label: "playlist_item.remove" },
  { value: "playlist_item.move", label: "playlist_item.move" },
  { value: "caption.upload", label: "caption.upload" },
  { value: "caption.delete", label: "caption.delete" },
  { value: "oauth.connect", label: "oauth.connect" },
  { value: "oauth.disconnect", label: "oauth.disconnect" },
];

const PAGE_SIZE = 50;

function AuditSection() {
  const [params, setParams] = useSearchParams();
  const action = params.get("action") ?? "";
  const offset = Math.max(0, Number(params.get("offset") ?? "0"));
  const [openId, setOpenId] = useState<number | null>(null);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  const q = useYoutubeAudit({ limit: PAGE_SIZE, offset, action });
  const entries = q.data?.entries ?? [];
  const total = q.data?.total ?? 0;

  return (
    <section className="mt-8">
      <header className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="page-eyebrow">Audit log</p>
          <p className="font-display-italic text-[1.15rem] text-[var(--color-ink)]">
            Every operation, recorded.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="chip chip-select">
            <span className="chip-label">Action</span>
            <select
              value={action}
              onChange={(e) => setParam("action", e.target.value || null)}
              aria-label="Filter by action"
            >
              {ACTION_FILTERS.map((f) => (
                <option key={f.value || "all"} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <p className="yt-admin-count">
            {q.isLoading
              ? "Loading…"
              : `${Math.min(offset + 1, total)} – ${Math.min(offset + entries.length, total)} of ${total}`}
          </p>
        </div>
      </header>

      {q.isLoading ? (
        <p className="yt-admin-empty">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="yt-admin-empty">No audit entries.</p>
      ) : (
        <ul className="yt-audit-table">
          {entries.map((e) => {
            const isOpen = openId === e.id;
            return (
              <li
                key={e.id}
                className={`yt-audit-table-row ${isOpen ? "is-open" : ""}`}
              >
                <button
                  type="button"
                  className="yt-audit-table-summary"
                  onClick={() => setOpenId(isOpen ? null : e.id)}
                  aria-expanded={isOpen}
                >
                  <span
                    className={`yt-audit-dot ${e.ok ? "is-ok" : "is-err"}`}
                    aria-hidden
                  />
                  <span className="yt-audit-when tabular">
                    {formatRelative(e.createdAt)}
                  </span>
                  <span className="yt-audit-action">{e.action}</span>
                  <span className="yt-audit-target font-grotesk text-[0.66rem]">
                    {e.targetType}
                    {e.targetId && (
                      <>
                        <span className="yt-row-sep">·</span>
                        {e.targetId}
                      </>
                    )}
                  </span>
                  <span className="yt-audit-actor">
                    {e.userName ?? <em className="font-display-italic">cron</em>}
                  </span>
                </button>
                {isOpen && (
                  <div className="yt-audit-detail">
                    <p className="page-eyebrow mb-1">Created</p>
                    <p className="font-grotesk text-[0.78rem] mb-3">
                      {new Date(e.createdAt).toLocaleString()}
                    </p>
                    {e.errorMessage && (
                      <>
                        <p className="page-eyebrow mb-1 text-[var(--color-violet-deep)]">
                          Error
                        </p>
                        <pre className="yt-audit-pre">{e.errorMessage}</pre>
                      </>
                    )}
                    {e.payloadJson && (
                      <>
                        <p className="page-eyebrow mb-1 mt-3">Payload</p>
                        <pre className="yt-audit-pre">
                          {prettifyJson(e.payloadJson)}
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <nav className="yt-pagination" aria-label="Audit pagination">
          <button
            type="button"
            className="btn-app-ghost"
            onClick={() =>
              setParam("offset", String(Math.max(0, offset - PAGE_SIZE)))
            }
            disabled={offset <= 0}
          >
            ← Prev
          </button>
          <button
            type="button"
            className="btn-app-ghost"
            onClick={() => setParam("offset", String(offset + PAGE_SIZE))}
            disabled={offset + PAGE_SIZE >= total}
          >
            Next →
          </button>
        </nav>
      )}
    </section>
  );
}

function prettifyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
